import {
  fetchMySubscription,
  fetchPartnerUserById,
  fetchUserDocuments,
} from "../api/partner/partnerApi";

/**
 * Get required document keys based on partner service_id
 * Service ID 2 (Real Estate / Broker / Channel Partner) requires: ["aadhar", "pan", "reraregistration"]
 * All other services require: ["aadhar", "pan"]
 */
export const getRequiredDocuments = (serviceId) => {
  const sid = String(serviceId || "").trim();
  return sid === "2"
    ? ["aadhar", "pan", "reraregistration"]
    : ["aadhar", "pan"];
};

/**
 * Human-readable document titles
 */
export const DOCUMENT_TITLES = {
  aadhar: "Aadhaar Card",
  pan: "PAN Card",
  reraregistration: "RERA Registration Certificate",
  rera: "RERA Registration Certificate",
};

/**
 * Normalizes document name key
 */
export const normalizeDocKey = (rawName) => {
  if (!rawName) return "";
  const name = String(rawName).trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (name.includes("aadhar") || name.includes("adhaar") || name.includes("adhar")) {
    return "aadhar";
  }
  if (name.includes("pan")) {
    return "pan";
  }
  if (name.includes("rera")) {
    return "reraregistration";
  }
  return name;
};

/**
 * Evaluate if required documents are complete.
 * Following mobile implementation (uploaded.some), at least ONE valid KYC document
 * (Aadhaar, PAN, or RERA) is required to fulfill the partner KYC document requirement.
 */
export const checkDocumentsStatus = (userData, customDocList = null) => {
  const serviceId = userData?.service_id || userData?.service?.id || "1";
  const required = getRequiredDocuments(serviceId);

  const docItems = Array.isArray(customDocList) && customDocList.length > 0
    ? customDocList
    : (Array.isArray(userData?.documents) ? userData.documents : []);

  const uploadedDocsMap = {};

  docItems.forEach((doc) => {
    const rawKey = doc?.name || doc?.document_name || doc?.type || "";
    const key = normalizeDocKey(rawKey);
    if (key) {
      uploadedDocsMap[key] = {
        ...doc,
        name: key,
        document_url: doc.document_url || doc.file_url || doc.url || "",
        uploaded: true,
      };
    }
  });

  const uploadedCount = Object.keys(uploadedDocsMap).length;
  // At least one valid KYC document uploaded satisfies the requirement
  const isComplete = uploadedCount > 0 || required.some((reqKey) => Boolean(uploadedDocsMap[reqKey]));
  const missingDocs = required.filter((reqKey) => !uploadedDocsMap[reqKey]);

  return {
    isComplete,
    hasAtLeastOneDoc: isComplete,
    uploadedCount,
    requiredDocs: required,
    missingDocs,
    uploadedDocsMap,
    serviceId: String(serviceId),
  };
};

/**
 * Access guard check for Partner dashboard
 * Checks:
 * 1. Subscription active & not expired
 * 2. Required KYC documents uploaded
 */
export const checkPartnerAccess = async ({ token, userId }) => {
  try {
    const [subRes, userRes, docsRes] = await Promise.allSettled([
      fetchMySubscription(token),
      userId ? fetchPartnerUserById(token, userId) : Promise.resolve(null),
      fetchUserDocuments(token),
    ]);

    let subscription = null;
    let isSubExpired = false;
    let isSubPending = false;
    let isSubActive = false;
    let subStatusType = "SUCCESS";

    if (subRes.status === "fulfilled") {
      const data = subRes.value;
      subStatusType = data?.statusType?.trim()?.toUpperCase() || "SUCCESS";
      subscription = data?.ahdUserSubscription || data?.subscription || null;

      const subStatus = subscription?.status?.trim()?.toUpperCase();
      const isActive = Boolean(subscription?.active);

      if (subStatusType === "FAILURE" || !subscription) {
        isSubExpired = true;
      } else if (subStatus === "EXPIRED" || !isActive) {
        isSubExpired = true;
      } else if (subStatus === "PENDING") {
        isSubPending = true;
      } else if (subStatus === "ACTIVE") {
        isSubActive = true;
      }
    } else {
      isSubExpired = true;
    }

    let user = null;
    let assetUrl = "";
    if (userRes.status === "fulfilled" && userRes.value) {
      user = userRes.value?.user || userRes.value?.data?.user || userRes.value?.data || null;
      assetUrl = userRes.value?.assetUrl || userRes.value?.data?.assetUrl || "";
    }

    let documentsList = [];
    if (docsRes.status === "fulfilled" && docsRes.value) {
      documentsList = docsRes.value?.documents || docsRes.value?.data || [];
    }

    // Check documents condition
    const docStatus = checkDocumentsStatus(user, documentsList);

    const allowAccess = isSubActive && docStatus.isComplete;

    return {
      allowAccess,
      subscription,
      isSubExpired,
      isSubPending,
      isSubActive,
      docStatus,
      user,
      assetUrl,
    };
  } catch (error) {
    console.error("Partner guard check error:", error);
    return {
      allowAccess: false,
      isSubExpired: true,
      isSubPending: false,
      isSubActive: false,
      docStatus: {
        isComplete: false,
        requiredDocs: ["aadhar", "pan"],
        missingDocs: ["aadhar", "pan"],
        uploadedDocsMap: {},
        serviceId: "1",
      },
      user: null,
      assetUrl: "",
    };
  }
};

export default {
  getRequiredDocuments,
  DOCUMENT_TITLES,
  normalizeDocKey,
  checkDocumentsStatus,
  checkPartnerAccess,
};
